<template>
  <div class="container-fluid">
    <PageHeader
      :page="page"
      has-return
      left-side-class="col-xl-8"
      right-side-class="col-xl-4 d-flex align-items-end justify-content-end pb-2"
      @onReturn="() => $router.go(-1)"
    />
    <FilterManager
      ref="filterManager"
      v-model="filters"
      :filters="filtersHeader"
      :display-advanced="false"
      @academicYear="onAcademicYearChanged"
      @region="onRegionChanged"
      @municipality="onMunicipalityChanged"
      @search="onSearchHandler"
    />

    <CustomSeparator
      text="Resultados"
      class="my-4 custom-separator-line"
    />

    <template v-if="alternatives.length">
      <ResultRow
        v-for="item in alternatives"
        :key="`row-item-${item.academicYear.id}-${item.school.id}`"
        ref="alternative-result-row"
        :value="item"
      />
    </template>

    <CustomResultNotFound
      v-else
      class="mt-4 row pt-3"
      text="No hay información disponible."
    />

    <Pagination
      v-if="pagination.totalItemCount > pagination.perPage"
      :page-size="pagination.perPage"
      :data="pagination"
      @pagination-go-page="onPageChanged"
    />
  </div>
</template>

<script>
import PageHeader from 'PageHeader';
import ActionButton from 'ActionButton';
import Pagination from 'pagination';
import FilterManager from 'FilterManager';
import CustomSeparator from 'custom-separator';
import CustomResultNotFound from 'custom-result-not-found';
import ResultRow from './components/OtherSchoolEnrollmentResultRow';
import academicYearService from '@/services/academicyear';
import locationService from '@/services/api/location.api.js';
import storeMixin from '@/mixin/storeGenerator';
import alert from '@/mixin/alert.mixin';
import SchoolTypes from '@/utils/constants/student/schoolTypes';
import {
  getLocationSchools as _getLocationSchools,
} from '@/modules/alternative-location-management/api';

const DEFAULT_PAGE_SIZE = 20;
const excludeUserLocations = ({
  skipUserCentersFlag: true,
  skipUserRegionsFlag: true,
  skipUserDistrictsFlag: true,
  skipUserMunicipalitiesFlag: true,
  skipUserSchoolCodesFlag: true,
  skipUserSchoolsFlag: true,
  excludeUserRegionsFlag: true,
  excludeUserMunicipalitiesFlag: true,
  excludeUserSchoolCodesFlag: true,
});

export default {
  name: 'OtherSchoolEnrollment',
  mixins: [storeMixin, alert],
  components: {
    PageHeader,
    ActionButton,
    Pagination,
    FilterManager,
    CustomResultNotFound,
    CustomSeparator,
    ResultRow,
  },
  data() {
    return {
      page: {
        icon: 'fas fa-user-edit',
        title: 'Consulta Matricula Otra Escuela',
        routes: [
          'Inicio',
          'Recursos Humanos',
          'Manejo de Alternativa de Ubicación',
        ],
      },
      collections: {
        academicYears: [],
        regions: [],
        municipalities: [],
        schools: [],
      },
      filters: {
        academicYear: null,
        region: null,
        municipality: null,
        school: null,
      },
      pagination: {
        perPage: DEFAULT_PAGE_SIZE,
        currentPage: 1,
        totalItemCount: 0,
        pageCount: 0,
        count: 0,
        data: [],
      },
    };
  },
  async created() {
    await this.fetchResources();
  },
  computed: {
    isMobile() {
      return this.$store.getters.getIsMobileDensity;
    },
    filtersHeader() {
      return [
        {
          name: 'academicYear',
          component: 'CustomDropDown',
          options: this.collections.academicYears,
          value: this.filters.academicYear,
          placeholder: 'Seleccione el año',
          fieldName: 'description',
          fieldKey: 'id',
          clearable: false,
          class: 'col-sm-6 col-md-6 col-lg-3 ml-0 mr-0',
          classContainer: 'w-100',
          validations: 'required',
          mapParentValue: this.filters.academicYear == null,
          error: 'Favor de seleccionar el año académico.',
        },
        {
          name: 'region',
          component: 'CustomDropDown',
          options: this.collections.regions,
          value: this.filters.region,
          placeholder: 'Región',
          fieldName: 'name',
          fieldKey: 'id',
          clearable: true,
          class: 'col-sm-6 col-md-6 col-lg-3 ml-0 mr-0',
          classContainer: 'w-100',
          mapParentValue: this.filters.region == null,
        },
        {
          name: 'municipality',
          component: 'CustomDropDown',
          options: this.collections.municipalities,
          value: this.filters.municipality,
          placeholder: 'Municipio',
          fieldName: 'name',
          fieldKey: 'id',
          clearable: true,
          class: 'col-sm-6 col-md-6 col-lg-3 ml-0 mr-0',
          classContainer: 'w-100',
          mapParentValue: this.filters.municipality == null,
        },
        {
          name: 'school',
          component: 'CustomDropDown',
          options: this.collections.schools,
          value: this.filters.school,
          placeholder: 'Escuela',
          fieldName: 'name',
          fieldKey: 'id',
          clearable: true,
          class: 'col-sm-6 col-md-6 col-lg-3 ml-0 mr-0',
          classContainer: 'w-100',
          mapParentValue: this.filters.school == null,
        },
      ];
    },
    alternatives() {
      return this.pagination?.data || [];
    },
  },
  methods: {
    async fetchResources() {
      const {
        data: academicYearList,
      } = await academicYearService.getAcademicYearsToCurrentYear();

      this.collections.academicYears = academicYearList;
    },
    async onAcademicYearChanged() {
      await this.getRegions();
    },
    async onRegionChanged(region) {
      if(!region) return;
      await this.getMunicipalities();
    },
    async onMunicipalityChanged(municipality) {
      if(!municipality) return;
      await this.getSchools();
    },
    async getRegions() {
      const { data: regionList } = await locationService.getRegions({
        ...excludeUserLocations,
        academicYearId: this.filters.academicYear?.id,
      });
      this.collections.regions = regionList;
    },
    async getMunicipalities() {
      const { data: municipalityList } = await locationService.getMunicipalities({
        ...excludeUserLocations,
        academicYearId: this.filters.academicYear?.id,
        regionId: this.filters.region?.id,
      });
      this.collections.municipalities = municipalityList;
    },
    async getSchools() {
      const { data: schoolList } = await locationService.getSchools({
        ...excludeUserLocations,
        academicYearId: this.filters.academicYear?.id,
        regionId: this.filters.region?.id,
        municipalityId: this.filters.municipality?.id,
        schoolTypes: [SchoolTypes.PublicSchool, SchoolTypes.Institutions],
      });
      this.collections.schools = schoolList;
    },
    async onPageChanged(currentPage) {
      this.pagination.currentPage = currentPage;
      await this.onSearch(currentPage);
    },
    async onSearchHandler() {
      await this.onSearch();
    },
    async onSearch(currentPage = 1) {
      if(!this.filters.academicYear?.id) return;

      const payload = {
        academicYearId: this.filters.academicYear?.id,
        regionId: this.filters.region?.id,
        municipalityId: this.filters.municipality?.id,
        schoolId: this.filters.school?.id,
        pageIndex: currentPage,
      };

      const { data: paginationResult } = await _getLocationSchools(payload);

      this.pagination = {
        ...this.pagination,
        totalItemCount: paginationResult.totalItemCount,
        pageCount: paginationResult.pageCount,
        currentPage: paginationResult.currentPage,
        count: paginationResult.count,
        data: paginationResult.data,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep .vs__dropdown-toggle {
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
